/**
 * <product-addon>: one optional add-on product (Greeting Card, Bottle Engraving)
 * offered inside the product form. Owns the checkbox and its fields only.
 * product-form.js reads `selected`, `variantId`, `mode` and `properties` from
 * every instance in the form when building the multi-item add.
 *
 * Fields are `disabled` while the add-on is unchecked, which keeps them out of
 * both FormData and native `required` validation. Field types: text, textarea,
 * number, email, tel, date, color, select, radio and checkbox (multi-select,
 * values joined with a comma).
 */
if (!customElements.get('product-addon')) {
  class ProductAddon extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.checkbox = this.querySelector('.product-addon__checkbox');
      this.fieldsWrap = this.querySelector('.product-addon__fields');
      this.fields = Array.from(this.querySelectorAll('[data-addon-field]'));
      this.requiredGroups = Array.from(this.querySelectorAll('[data-addon-required-group]'));
      this.variantSelect = this.querySelector('[data-addon-variant-select]');
      this.priceEl = this.querySelector('[data-addon-price]');

      if (this.checkbox) {
        this.checkbox.addEventListener('change', this.sync.bind(this));
      }

      if (this.variantSelect) {
        this.variantSelect.addEventListener('change', this.updatePrice.bind(this));
        this.updatePrice();
      }

      this.fields.forEach((field) => {
        field.addEventListener('input', () => ProductAddon.updateCount(field));
        field.addEventListener('change', this.validateGroups.bind(this));
        ProductAddon.updateCount(field);
      });

      this.sync();
    }

    get selected() {
      return Boolean(this.checkbox && this.checkbox.checked);
    }

    /**
     * Variant to add: the picker's selection when the add-on product has real
     * variants, otherwise the first available variant rendered on the element.
     */
    get variantId() {
      if (this.variantSelect && this.variantSelect.value) return this.variantSelect.value;
      return this.dataset.addonVariantId;
    }

    /**
     * Shows the selected variant's price after the label.
     */
    updatePrice() {
      if (!this.variantSelect || !this.priceEl) return;
      const option = this.variantSelect.options[this.variantSelect.selectedIndex];
      if (option && option.dataset.price) this.priceEl.textContent = `+${option.dataset.price}`;
    }

    get mode() {
      return this.dataset.addonMode === 'match' ? 'match' : 'single';
    }

    get title() {
      return this.dataset.addonTitle || '';
    }

    /**
     * Title of the product this add-on is offered on (the bottle).
     */
    get parentTitle() {
      return this.dataset.parentTitle || '';
    }

    /**
     * Property names of every field (the `properties[...]` input name with the
     * wrapper stripped), whether or not the field has a value.
     */
    get propertyNames() {
      const names = new Set();
      this.fields.forEach((field) => {
        const match = field.name.match(/^properties\[(.+)\]$/);
        if (match) names.add(match[1]);
      });
      return Array.from(names);
    }

    /**
     * Trimmed field values keyed by property name (the `properties[...]` input
     * name with the wrapper stripped). Radios and checkboxes contribute only when
     * checked; several checked checkboxes under one name are joined with commas.
     * Empty values are left out.
     */
    get properties() {
      const values = {};
      this.fields.forEach((field) => {
        const match = field.name.match(/^properties\[(.+)\]$/);
        if (!match) return;
        if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
        const value = field.value.trim();
        if (!value) return;
        (values[match[1]] = values[match[1]] || []).push(value);
      });

      const props = {};
      Object.keys(values).forEach((name) => {
        props[name] = values[name].join(', ');
      });
      return props;
    }

    /**
     * Shows or hides the fields and enables or disables their inputs to match the
     * checkbox state.
     */
    sync() {
      const on = this.selected;
      if (this.fieldsWrap) this.fieldsWrap.hidden = !on;
      this.fields.forEach((field) => {
        field.disabled = !on;
      });
      this.classList.toggle('is-selected', on);
      this.validateGroups();
    }

    /**
     * A required checkbox group needs at least one box ticked. Native `required`
     * would demand every box, so the rule is expressed as a custom validity on
     * the group's first box (which the browser then reports on submit).
     */
    validateGroups() {
      this.requiredGroups.forEach((group) => {
        const boxes = Array.from(group.querySelectorAll('input[type="checkbox"]'));
        if (!boxes.length) return;
        const satisfied = !this.selected || boxes.some((box) => box.checked);
        boxes[0].setCustomValidity(satisfied ? '' : theme.strings.addonChooseOne);
      });
    }

    /**
     * Unchecks the add-on and clears its fields (used after a successful add).
     */
    reset() {
      if (this.checkbox) this.checkbox.checked = false;
      this.fields.forEach((field) => {
        if (field.type === 'checkbox' || field.type === 'radio') {
          field.checked = false;
        } else if (field.tagName === 'SELECT') {
          // The variant picker keeps its selection; option pickers go back to blank.
          if (field !== this.variantSelect) field.selectedIndex = 0;
        } else {
          field.value = '';
        }
        ProductAddon.updateCount(field);
      });
      this.sync();
    }

    static updateCount(field) {
      const wrap = field.closest('[data-addon-field-group]');
      const count = wrap ? wrap.querySelector('[data-addon-count]') : null;
      if (count) count.textContent = field.value.length;
    }
  }

  customElements.define('product-addon', ProductAddon);
}
